import {
	ReactElement,
	RefObject,
	useRef,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { NewAppModel } from "client/Api/Apps/AppStore/appStore";
import {
	ContentWithSidebar,
	Dialog,
	SearchInputForm,
	Toolbar
} from "client/Components";
import { AppCard } from "./AppCard";
import { AppCardList } from "./AppCardList";
import { useScrollToTop } from "client/Hooks";
import { allAppsCategory, getCategoryName } from "./CategoryFilter";
import { generateTrackingId } from "client/Misc/utils";
import { AppTarget } from "./EditApp/EditAppDialogContent";
import { AppStoreLayout } from "./AppStoreLayout";

export type AddNewAppProps = {
	apps: readonly NewAppModel[];
	onEditApp(
		target: AppTarget,
		activateWhoHasAccessTab: boolean
	): void;
	onBack: () => void;
	onClose: () => void;
};

export const AddNewApp = (props: AddNewAppProps): ReactElement => {
	const {
		apps,
		onEditApp,
		onBack,
		onClose
	} = props;

	const [searchTerm, setSearchTerm] = useState<string>("");
	const contentRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	useScrollToTop(
		contentRef,
		[searchTerm]
	);

	const normalizedSearchTerm: string = searchTerm.toUpperCase();
	const appsToRender: NewAppModel[] = apps.filter(
		(app) => {
			if (searchTerm.length > 0) {
				const matchesName: boolean = !app.name
					|| app.name.toUpperCase().indexOf(normalizedSearchTerm) !== -1;
				if (!matchesName) {
					return false;
				}
			}

			return true;
		}
	);

	const addHandler = (app: NewAppModel): void => {
		onEditApp(
			{
				kind: "new-environment-app",
				appType: app.appType
			},
			false
		);
	};

	return (
		<>
			<Dialog.Header
				hasBorder
				onClose={onClose}
				onBack={onBack}
				title={localize("_AddNewApp")}
			/>
			<Dialog.Body goesEdgeToEdge>
				<ContentWithSidebar trackingId="add-new-app">
					<ContentWithSidebar.Content ref={contentRef} hasBackgroundColor>
						<AppStoreLayout>
							<AppStoreLayout.Header
								search={
									<SearchInputForm
										onChange={setSearchTerm}
										placeholder={localize("_SearchApps")}
										trackingId="search-term"
									/>
								}
							/>

							<AppStoreLayout.CategoryName name={allAppsCategory.name} />

							{appsToRender.length === 0
								? <p>{localize("_NoResultsFound")}</p>
								: (
									<AppCardList trackingId="card-list">
										{appsToRender.map((x) => (
											<AppCard
												key={`${x.appType}`}
												icon={x.imageDisplayInfo}
												color={x.color}
												userLicense={x.userLicense}
												name={x.name}
												category={getCategoryName(x.categoryId)}
												size="short"
												primaryAction={{
													key: "_Add",
													kind: "primary",
													handler: () => addHandler(x),
													trackingId: "add"
												}}
												trackingId={generateTrackingId("add-new-app-card", `|${x.appType}`)}
											/>
										))}
									</AppCardList>
								)
							}
						</AppStoreLayout>
					</ContentWithSidebar.Content>
				</ContentWithSidebar>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="secondary"
						onClick={onBack}
						text={localize("_Back")}
						responsiveBehavior="hide"
					/>
					<Toolbar.Button
						kind="secondary"
						onClick={onClose}
						text={localize("_Close")}
						responsiveBehavior="hide"
					/>
				</Toolbar>
			</Dialog.Footer>
		</>
	);
};

AddNewApp.displayName = "AddNewApp";
